// The Deck (prototype): one persistent player bar for the whole site. See snippets/pr-deck.liquid.
// window.PRDeck is used by the Needle Drop hero (assets/pr-needle.js):
//   load(track, { play, from, delay }): the visitor put a record on (plays it, flies the disc in)
//   cue(track) / uncue(): the hero scrolled away and the record rolled onto the Deck, not playing
//   approach(p): the disc is on its way (0–1), so the empty bar fades in
//   slotRect(), current()
// A track is { kind: 'mp3' | 'bandcamp' | 'link', src, sample, title, artist, catalog, url, disc }.
// Also handles click-to-load embeds anywhere on the page ([data-pr-embed]), and exposes
// window.PRVinyl.restyle(svg, name): redraw a snippets/pr-vinyl disc in another pressing colour.

// ---- Disc colour from a pressing name (mirrors snippets/pr-vinyl.liquid) ----
(() => {
  const COLORS = [
    [/yellow/, '#FFD43B'], [/tangerine|orange/, '#F28C28'], [/red/, '#D7263D'],
    [/lilac|purple|violet/, '#B99BDB'], [/blue/, '#2E6FD8'], [/green/, '#3E9B5B'], [/pink/, '#F4A6C0'],
    [/gold/, '#C9A227'], [/white|bone|cream/, '#ECEBE6'], [/grey|gray|smoke|silver/, '#8C8B86'],
    [/clear|transparent/, '#D9D9D6'],
  ];
  window.PRVinyl = {
    restyle(svg, name) {
      if (!svg) return;
      const n = String(name || 'black').toLowerCase();
      const style = /marble|swirl/.test(n) ? 'marble' : /splatter|splash/.test(n) ? 'splatter'
        : /translucent|clear|transparent/.test(n) ? 'translucent' : 'solid';
      const hit = COLORS.find(([re]) => re.test(n));
      svg.style.setProperty('--pr-vinyl', hit ? hit[1] : '#111111');
      svg.dataset.style = style;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      svg.animate([{ scale: 0.94 }, { scale: 1 }], { duration: 220, easing: 'steps(3, end)' });
    },
  };
})();

(() => {
  const el = document.querySelector('[data-pr-deck]');
  if (!el) return;

  const KEY = 'pr-deck';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const q = (s) => el.querySelector(s);
  const slot = q('[data-pr-deck-slot]');
  const discEl = q('[data-pr-deck-disc]');
  const link = q('[data-pr-deck-link]');
  const artistEl = q('[data-pr-deck-artist]');
  const player = q('[data-pr-deck-player]');
  const buy = q('[data-pr-deck-buy]');
  const progress = q('[data-pr-deck-progress]');

  let strings = {};
  try {
    strings = JSON.parse(el.dataset.strings || '{}');
  } catch (e) {}
  const t = (key, vars = {}) => (strings[key] || '').replace(/%(\w+)%/g, (_, name) => vars[name] ?? '');

  let track = null;
  let audio = null;
  let savedAt = 0;

  const emit = () => document.dispatchEvent(new CustomEvent('pr:deck', { detail: { track } }));

  const save = () => {
    try {
      if (!track || track.cuedBy === 'scroll') {
        sessionStorage.removeItem(KEY);
        return;
      }
      const playing = !!audio && !audio.paused && audio.dataset.src === track.src;
      const at = audio && audio.dataset.src === track.src ? audio.currentTime : track.t || 0;
      sessionStorage.setItem(KEY, JSON.stringify({ ...track, t: at, playing }));
    } catch (e) {}
  };

  const fmt = (s) => {
    const n = Math.max(0, Math.floor(s || 0));
    return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
  };

  const serviceOf = (url) =>
    /bandcamp/i.test(url) ? 'Bandcamp' : /soundcloud/i.test(url) ? 'SoundCloud' : /spotify/i.test(url) ? 'Spotify' : 'the web';

  // ---- Rendering ----
  const renderMeta = () => {
    discEl.innerHTML = (track.disc || '').replace(/pr-vinyl-(clip|splat|marble|runout)-/g, 'pr-vinyl-$1-deck-');
    link.textContent = track.title;
    link.href = track.url;
    artistEl.textContent = [track.artist, track.catalog].filter(Boolean).join(' · ');
    buy.href = track.url;
    buy.setAttribute('aria-label', t('buy_label', { title: track.title }));
    progress.style.width = '0%';
  };

  const button = (cls, label, attr) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.setAttribute(attr, '');
    b.innerHTML = '<span aria-hidden="true">▶</span>';
    b.append(document.createTextNode(label ? ` ${label}` : ''));
    return b;
  };

  const note = (text) => {
    const span = document.createElement('span');
    span.className = 'pr-deck__via';
    span.textContent = text;
    return span;
  };

  const mp3Controls = () => {
    const toggle = button('pr-deck__play', '', 'data-pr-deck-toggle');
    toggle.setAttribute('aria-label', t('play'));
    const time = document.createElement('span');
    time.className = 'pr-deck__time';
    time.setAttribute('data-pr-deck-time', '');
    time.textContent = fmt(track.t);
    player.replaceChildren(toggle, time);
    if (track.sample) player.append(note(t('sample_audio')));
  };

  const bandcampFacade = (label) => {
    player.replaceChildren(button('pr-deck__load', label, 'data-pr-deck-load'), note(t('loads_bandcamp')));
  };

  const bandcampFrame = () => {
    const frame = document.createElement('iframe');
    frame.className = 'pr-deck__frame';
    frame.title = t('bandcamp_title', { title: track.title });
    frame.src = `https://bandcamp.com/EmbeddedPlayer/${track.src}/size=small/bgcol=0e0e0d/linkcol=f4d34f/transparent=true/`;
    player.replaceChildren(frame);
  };

  const linkOut = () => {
    const a = document.createElement('a');
    a.className = 'pr-deck__load';
    a.href = track.src;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = `${t('listen_on', { service: serviceOf(track.src) })} ↗`;
    player.replaceChildren(a);
  };

  // ---- Audio (preview files) ----
  const onState = () => {
    const playing = !!audio && !audio.paused;
    el.classList.toggle('is-playing', playing);
    if (playing) el.classList.remove('needs-tap');
    const toggle = q('[data-pr-deck-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-label', playing ? t('pause') : t('play'));
      toggle.firstElementChild.textContent = playing ? '❚❚' : '▶';
    }
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    save();
  };

  const onTime = () => {
    const time = q('[data-pr-deck-time]');
    if (time) time.textContent = fmt(audio.currentTime);
    if (audio.duration) progress.style.width = `${(audio.currentTime / audio.duration) * 100}%`;
    const now = Date.now();
    if (now - savedAt > 1000) {
      savedAt = now;
      save();
    }
  };

  const ensureAudio = () => {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';
    ['play', 'pause', 'ended'].forEach((type) => audio.addEventListener(type, onState));
    audio.addEventListener('timeupdate', onTime);
    return audio;
  };

  const mediaSession = () => {
    if (!('mediaSession' in navigator) || !window.MediaMetadata) return;
    navigator.mediaSession.metadata = new MediaMetadata({ title: track.title, artist: track.artist, album: track.catalog });
    navigator.mediaSession.setActionHandler('play', () => audio?.play());
    navigator.mediaSession.setActionHandler('pause', () => audio?.pause());
  };

  const playMp3 = () => {
    const a = ensureAudio();
    let at = track.t || 0;
    if (a.dataset.src !== track.src) {
      a.src = track.src;
      a.dataset.src = track.src;
      a.loop = !!track.sample;
    } else {
      at = a.currentTime;
    }
    if (at) {
      const seek = () => {
        try {
          a.currentTime = at;
        } catch (e) {}
      };
      if (a.readyState >= 1) seek();
      else a.addEventListener('loadedmetadata', seek, { once: true });
    }
    const started = a.play();
    if (started) {
      started.catch(() => {
        // The browser wants a tap first (typical right after a page change)
        el.classList.add('needs-tap');
        onState();
      });
    }
    mediaSession();
  };

  const stop = () => {
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      delete audio.dataset.src;
      audio.load();
    }
    el.classList.remove('is-playing', 'needs-tap');
    player.replaceChildren();
  };

  // ---- Showing and hiding ----
  const show = () => {
    el.classList.add('is-on');
    el.classList.remove('is-arriving');
    el.style.removeProperty('--arrive');
    document.documentElement.classList.add('pr-deck-on');
  };

  const hide = () => {
    el.classList.remove('is-on', 'is-arriving');
    document.documentElement.classList.remove('pr-deck-on');
  };

  const setup = (next, cuedBy, play) => {
    stop();
    track = { ...next, cuedBy, t: 0 };
    renderMeta();
    if (track.kind === 'mp3') {
      mp3Controls();
      if (play) playMp3();
    } else if (track.kind === 'bandcamp') {
      if (play) bandcampFrame();
      else bandcampFacade(t('play_on', { service: 'Bandcamp' }));
    } else {
      linkOut();
    }
  };

  // The disc lifts off the hero and lands in the slot
  const fly = (from, delay) => {
    if (!from || reduce.matches) return;
    const a = from.getBoundingClientRect();
    const b = slot.getBoundingClientRect();
    if (!a.width || !b.width) return;
    const dx = a.left + a.width / 2 - (b.left + b.width / 2);
    const dy = a.top + a.height / 2 - (b.top + b.height / 2);
    const start = `translate(${dx}px, ${dy}px) scale(${a.width / b.width})`;
    const total = delay + 760;
    discEl.animate(
      [
        { transform: start, opacity: 0 },
        { transform: start, opacity: 0, offset: delay / total },
        { transform: start, opacity: 1, offset: (delay + 1) / total },
        { transform: 'translate(0, 0) scale(1) rotate(-360deg)', opacity: 1 },
      ],
      { duration: total, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
    );
  };

  const claim = () => {
    if (track && track.cuedBy === 'scroll') {
      track.cuedBy = 'user';
      save();
      emit();
    }
  };

  const api = {
    current: () => track,
    slotRect: () => slot.getBoundingClientRect(),
    approach(p) {
      if (track) return;
      const arrive = Math.min(1, Math.max(0, (p - 0.45) / 0.4));
      el.classList.toggle('is-arriving', p > 0.45);
      el.style.setProperty('--arrive', String(arrive));
    },
    cue(next) {
      if (track && track.cuedBy !== 'scroll') return;
      setup(next, 'scroll', false);
      show();
      emit();
    },
    uncue() {
      if (!track || track.cuedBy !== 'scroll') return;
      stop();
      track = null;
      hide();
      emit();
    },
    load(next, { play = true, from = null, delay = 0 } = {}) {
      setup(next, 'user', play);
      show();
      fly(from, delay);
      save();
      if (delay && !reduce.matches) setTimeout(emit, delay);
      else emit();
    },
    pause() {
      audio?.pause();
    },
  };

  el.addEventListener('click', (event) => {
    if (event.target.closest('[data-pr-deck-close]')) {
      stop();
      track = null;
      hide();
      save();
      emit();
      return;
    }
    if (event.target.closest('[data-pr-deck-toggle]')) {
      claim();
      if (!audio || audio.paused || audio.dataset.src !== track.src) playMp3();
      else audio.pause();
      return;
    }
    if (event.target.closest('[data-pr-deck-load]')) {
      claim();
      bandcampFrame();
      save();
    }
  });

  window.addEventListener('pagehide', save);

  // ---- Click-to-load embeds (no third-party request until the visitor asks) ----
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-pr-embed]');
    if (!trigger) return;
    const wrap = trigger.closest('[data-pr-embed-wrap]') || trigger.parentElement;
    const frame = document.createElement('iframe');
    frame.className = 'pr-embed__frame';
    frame.src = trigger.dataset.src;
    frame.title = trigger.dataset.title || '';
    frame.style.height = `${trigger.dataset.height || 152}px`;
    if (trigger.dataset.allow) frame.allow = trigger.dataset.allow;
    api.pause();
    wrap.replaceChildren(frame);
  });

  // ---- Coming back from another page ----
  let saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
  } catch (e) {}
  if (saved && saved.title) {
    track = { ...saved, cuedBy: 'user' };
    renderMeta();
    show();
    if (track.kind === 'mp3') {
      mp3Controls();
      if (saved.playing) playMp3();
    } else if (track.kind === 'bandcamp') {
      bandcampFacade(t('keep_listening'));
    } else {
      linkOut();
    }
  }

  window.PRDeck = api;
  document.dispatchEvent(new Event('pr:deck-ready'));
})();
